// timeGridLayout — чистые функции временной сетки календаря (U-39 V13, Tech-Story §12.1/§12.2).
// Единственный источник геометрии для TimeGridDay/TimeGridWeek и AllDayRow.
// ПОЗИЦИИ В %: top/height/left/width считаются от 0..1440 минут локального дня,
// что сохраняет корректность при ресайзе грида (position: absolute в %).

import { startOfDay, addDays, isSameDay } from 'date-fns';
import type { CalendarEvent } from '@/api/calendar';

/** Сутки в минутах (высота грида = 100%). */
export const MINUTES_PER_DAY = 24 * 60;
/** Высота часа в px (день = 56 * 24 = 1344px). */
export const HOUR_HEIGHT_PX = 56;
/** Снап-сетка 30 мин для создания события из слота (C1). */
export const SLOT_MINUTES = 30;
/** «+N ещё» в all-day ряду (S2). */
export const MAX_ALLDAY_ROW_VISIBLE = 3;
/** «+N ещё» в колонке дня (S2). */
export const MAX_VISIBLE_EVENTS_PER_COLUMN = 3;
/** Минимальная высота чипа в px (кликабельность; «рост вверх» — W5). */
export const MIN_EVENT_HEIGHT_PX = 24;
/** Минимальная высота чипа в % дня (≈1.79%). */
export const MIN_EVENT_HEIGHT_PCT = (MIN_EVENT_HEIGHT_PX / (HOUR_HEIGHT_PX * 24)) * 100;
/** Частота пересчёта NowLine (S1). */
export const NOW_LINE_REFRESH_MS = 60_000;

/** Локальные минуты дня (getHours*60+getMinutes) — Р1, не UTC-эпоха. */
function localMinutes(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Фрагмент события, приведённый к локальному дню (B2). */
export interface DayFragment {
  dayIndex: number;
  startMin: number;
  endMin: number;
  event: CalendarEvent;
}

/** Позиция чипа (все значения % от контейнера дня). */
export interface PositionedSpan extends DayFragment {
  topPct: number;
  heightPct: number;
  leftPct: number;
  widthPct: number;
  col: number;
  cols: number;
}

/** Позиция all-day чипа в неделе (span по колонкам, W2). */
export interface AllDayPosition {
  event: CalendarEvent;
  leftPct: number;
  widthPct: number;
}

/**
 * Event[] × days → фрагменты по локальным дням (сплит через локальную полночь).
 * Границы дня — startOfDay/addDays (DST-безопасно); нулевые фрагменты отбрасываются;
 * конец ровно в локальную полночь следующего дня → endMin = 1440.
 */
export function toDayFragments(events: CalendarEvent[], days: Date[]): DayFragment[] {
  const result: DayFragment[] = [];
  days.forEach((day, dayIndex) => {
    const dayStart = startOfDay(day); // локальная полночь дня
    const nextDayStart = addDays(dayStart, 1); // следующая локальная полночь (DST-безопасно)
    for (const event of events) {
      if (event.allDay) continue; // allDay — AllDayRow, не time-grid (W1-T6)
      const evStart = new Date(event.startAt);
      const evEnd = new Date(event.endAt);
      const fragmentStart = new Date(Math.max(evStart.getTime(), dayStart.getTime()));
      const fragmentEnd = new Date(Math.min(evEnd.getTime(), nextDayStart.getTime()));
      if (fragmentEnd.getTime() <= fragmentStart.getTime()) continue; // нулевые фрагменты отбрасываются
      const startMin = localMinutes(fragmentStart);
      const endMin =
        fragmentEnd.getTime() === nextDayStart.getTime()
          ? MINUTES_PER_DAY // граница «следующая полночь» → конец дня 1440
          : localMinutes(fragmentEnd);
      result.push({ dayIndex, startMin, endMin, event });
    }
  });
  return result;
}

/** Индекс локального дня события в массиве days (-1, если дня нет). */
export function dayIndexOf(date: Date, days: Date[]): number {
  return days.findIndex((d) => isSameDay(d, date));
}

/** Позиции all-day чипов: span по колонкам (clamp к границам диапазона, W2). */
export function allDaySpans(events: CalendarEvent[], days: Date[]): AllDayPosition[] {
  return events
    .filter((event) => event.allDay)
    .map((event) => {
      const startIdx = dayIndexOf(new Date(event.startAt), days);
      const endIdx = dayIndexOf(new Date(event.endAt), days);
      const left = Math.max(0, startIdx === -1 ? 0 : startIdx);
      const right = Math.min(days.length - 1, endIdx === -1 ? days.length - 1 : endIdx);
      return {
        event,
        leftPct: (left / days.length) * 100,
        widthPct: ((right - left + 1) / days.length) * 100,
      };
    });
}

/** Пересечение фрагментов (строгое: касание в точке — не пересечение, B1). */
function overlaps(a: DayFragment, b: DayFragment): boolean {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

/** Фаза 1: жадная раздача колонок (first-fit по последней точке конца). */
function assignColumns(fragments: DayFragment[], order: number[]): number[] {
  const colOf: number[] = new Array(fragments.length);
  const colEndAt: number[] = [];
  for (const idx of order) {
    // Индексы из order/cluster всегда 0..n-1, поэтому `as` корректен.
    const frag = fragments[idx] as DayFragment;
    let col = 0;
    // Пока колонка ещё занята событием, пересекающимся с текущим (её конец > его старт).
    while (col < colEndAt.length && (colEndAt[col] ?? 0) > frag.startMin) col += 1;
    colOf[idx] = col;
    colEndAt[col] = frag.endMin;
  }
  return colOf;
}

/** Фаза 2: кластеры связности (DFS по overlap-графу) — компоненты связности. */
function buildClusters(fragments: DayFragment[], n: number): Array<number[]> {
  const clusters: Array<number[]> = [];
  const visited: boolean[] = new Array(n).fill(false);
  for (let i = 0; i < n; i += 1) {
    if (visited[i]) continue;
    const cluster: number[] = [];
    const stack = [i];
    visited[i] = true;
    while (stack.length > 0) {
      const v = stack.pop() as number;
      cluster.push(v);
      for (let u = 0; u < n; u += 1) {
        if (!visited[u] && overlaps(fragments[v] as DayFragment, fragments[u] as DayFragment)) {
          visited[u] = true;
          stack.push(u);
        }
      }
    }
    clusters.push(cluster);
  }
  return clusters;
}

/** Фаза 3: позиции в % — равнодолевая ширина кластера + min-высота «рост вверх» (W5). */
function positionSpans(
  fragments: DayFragment[],
  colOf: number[],
  clusters: Array<number[]>,
): PositionedSpan[] {
  const result: PositionedSpan[] = new Array(fragments.length);
  for (const cluster of clusters) {
    let colsInCluster = 1;
    for (const idx of cluster) colsInCluster = Math.max(colsInCluster, (colOf[idx] ?? 0) + 1);
    const widthPct = 100 / colsInCluster;
    for (const idx of cluster) {
      const frag = fragments[idx] as DayFragment;
      const naturalPct = ((frag.endMin - frag.startMin) / MINUTES_PER_DAY) * 100;
      const heightPct = Math.max(naturalPct, MIN_EVENT_HEIGHT_PCT);
      result[idx] = {
        ...frag,
        topPct: Math.max(0, (frag.startMin / MINUTES_PER_DAY) * 100 - (heightPct - naturalPct)),
        heightPct,
        leftPct: ((colOf[idx] ?? 0) * 100) / colsInCluster,
        widthPct,
        col: colOf[idx] ?? 0,
        cols: colsInCluster,
      };
    }
  }
  return result;
}

/**
 * Раскладка фрагментов ОДНОГО дня (все dayIndex одинаковы): кластеры, столбцы, ширины в %.
 * Кластер = компонента связности overlap-графа; ширина делится равнодолево
 * (канон Google Calendar, B1): widthPct = 100 / colsInCluster.
 */
export function layoutDaySpans(fragments: DayFragment[]): PositionedSpan[] {
  const n = fragments.length;
  const order = fragments
    .map((_, i) => i)
    .sort((i, j) => {
      const a = fragments[i] as DayFragment;
      const b = fragments[j] as DayFragment;
      return a.startMin - b.startMin || b.endMin - a.endMin;
    });
  const colOf = assignColumns(fragments, order);
  const clusters = buildClusters(fragments, n);
  return positionSpans(fragments, colOf, clusters);
}
